import { AssetKeys } from "../assets";
import { GAME_WIDTH } from "../constants";
import { MAP_DECORATION_CONFIG } from "../mapDecorationConfig";
import type { MapModule, ObstacleData } from "../types";

interface RngLike {
  next(): number;
  range(min: number, max: number): number;
  int(min: number, max: number): number;
  pick<T>(items: T[]): T;
}

export class MapDecorationSystem {
  static decorateModule(module: MapModule, rng: RngLike, elapsed: number) {
    const density = MAP_DECORATION_CONFIG.density;
    if (density <= 0) return;

    let decoIdCounter = 0;
    const addDeco = (
      key: string,
      x: number,
      y: number,
      width: number,
      height: number,
      rotation: number,
      blocking: boolean,
      ySort = false
    ) => {
      // Avoid placing decorations completely off-screen horizontally
      const clampedX = Math.max(20, Math.min(GAME_WIDTH - 20, x));
      module.obstacles.push({
        id: `${module.id}-deco-${decoIdCounter++}`,
        key,
        x: clampedX,
        y,
        width,
        height,
        rotation,
        blocking,
        decorative: true,
        ySort
      } as ObstacleData & { ySort?: boolean });
    };

    // Helper to sample path center at a given Y
    const getPathX = (yVal: number) => {
      const points = module.safePath;
      for (let i = 0; i < points.length - 1; i += 1) {
        const a = points[i];
        const b = points[i + 1];
        if (yVal >= a.y && yVal <= b.y) {
          const t = (yVal - a.y) / (b.y - a.y);
          return a.x + (b.x - a.x) * t;
        }
      }
      return points[points.length - 1].x;
    };

    // ── 1. FOREST BORDERS ───────────────────────────────────────────────────
    // Place dense trees and roots at the left/right edges of the screen
    const borderSteps = Math.floor(6 * density);
    for (let i = 0; i < borderSteps; i += 1) {
      const t = i / (borderSteps - 1 || 1);
      const y = module.y + module.height * t + rng.range(-20, 20);

      // Left Border Tree
      const treeLeftKey = rng.pick([AssetKeys.obstacle.treeDead, AssetKeys.obstacle.treeTwisted, AssetKeys.obstacle.rootCurve]);
      const leftScale = rng.range(130, 180);
      addDeco(treeLeftKey, rng.range(24, 76), y, leftScale, leftScale * 1.2, rng.range(-0.18, 0.18), false, true);

      // Right Border Tree
      const treeRightKey = rng.pick([AssetKeys.obstacle.treeDead, AssetKeys.obstacle.treeTwisted, AssetKeys.obstacle.rootWall]);
      const rightScale = rng.range(130, 180);
      addDeco(treeRightKey, GAME_WIDTH - rng.range(24, 76), y, rightScale, rightScale * 1.2, rng.range(-0.18, 0.18), false, true);
    }

    // ── 2. RUIN CLUSTERS ────────────────────────────────────────────────────
    const ruinCount = Math.round(MAP_DECORATION_CONFIG.ruinClusterCount * density);
    for (let i = 0; i < ruinCount; i += 1) {
      const y = module.y + rng.range(module.height * 0.15, module.height * 0.85);
      const pathX = getPathX(y);
      const side = rng.pick([-1, 1]);
      const clusterX = pathX + side * rng.range(132, 224);

      // Center ruin object (altar or collapsed arch modeled with ruin/stump assets)
      const ruinKey = AssetKeys.obstacle.ruin;
      const ruinWidth = rng.range(140, 190);
      const ruinHeight = ruinWidth * 0.88;
      addDeco(ruinKey, clusterX, y, ruinWidth, ruinHeight, rng.range(-0.35, 0.35), false, true);

      // Collapsed column/stump nearby
      const columnKey = AssetKeys.obstacle.stump;
      const colSize = rng.range(68, 96);
      addDeco(columnKey, clusterX + rng.range(-46, 46), y + rng.range(28, 56), colSize, colSize, rng.range(-1.2, 1.2), false, true);

      // Luminous moss or green cracks surrounding the ruin
      const groundKey = rng.pick([AssetKeys.deco.moss, AssetKeys.deco.cracks]);
      const groundSize = rng.range(98, 148);
      addDeco(groundKey, clusterX + rng.range(-32, 32), y + rng.range(-18, 18), groundSize, groundSize * 0.8, rng.range(0, Math.PI * 2), false, false);

      // A small patch of glowing mushrooms
      if (rng.next() > 0.34) {
        const shroomSize = rng.range(42, 64);
        addDeco(AssetKeys.deco.mushrooms, clusterX + rng.range(-54, 54), y + rng.range(-42, -18), shroomSize, shroomSize, rng.range(-0.45, 0.45), false, false);
      }
    }

    // ── 3. STONE CLUSTERS ───────────────────────────────────────────────────
    const stoneCount = Math.round(MAP_DECORATION_CONFIG.stoneClusterCount * density);
    for (let i = 0; i < stoneCount; i += 1) {
      const y = module.y + rng.range(module.height * 0.1, module.height * 0.9);
      const pathX = getPathX(y);
      const side = rng.pick([-1, 1]);
      const clusterX = pathX + side * rng.range(126, 218);

      // Main big rock
      const mainRockSize = rng.range(110, 150);
      addDeco(AssetKeys.obstacle.rockHuge, clusterX, y, mainRockSize, mainRockSize * 0.84, rng.range(-0.5, 0.5), false, true);

      // Supporting smaller rock cluster
      if (rng.next() > 0.28) {
        const subRockSize = rng.range(62, 86);
        addDeco(AssetKeys.obstacle.rockCluster, clusterX + rng.range(-38, 38), y + rng.range(24, 48), subRockSize, subRockSize, rng.range(-0.6, 0.6), false, true);
      }

      // Surrounded by grass/moss
      const grassKey = rng.pick([AssetKeys.deco.grass, AssetKeys.deco.moss]);
      const grassSize = rng.range(82, 118);
      addDeco(grassKey, clusterX + rng.range(-28, 28), y + rng.range(-22, 22), grassSize, grassSize, rng.range(0, Math.PI * 2), false, false);
    }

    // ── 4. FLOWER AND MOSS PATCHES ──────────────────────────────────────────
    const flowerCount = Math.round(MAP_DECORATION_CONFIG.flowerPatchCount * density);
    for (let i = 0; i < flowerCount; i += 1) {
      const y = module.y + rng.range(module.height * 0.05, module.height * 0.95);
      const pathX = getPathX(y);
      // Place near path margins
      const clusterX = pathX + rng.pick([-1, 1]) * rng.range(76, 134);

      const grassKey = AssetKeys.deco.grass;
      const size = rng.range(64, 98);
      addDeco(grassKey, clusterX, y, size, size, rng.range(0, Math.PI * 2), false, false);

      // Occasional moss
      if (rng.next() > 0.45) {
        const mossKey = AssetKeys.deco.moss;
        const mossSize = rng.range(72, 112);
        addDeco(mossKey, clusterX + rng.range(-34, 34), y + rng.range(-24, 24), mossSize, mossSize, rng.range(0, Math.PI * 2), false, false);
      }
    }

    // ── 5. GLOWING DETAILS ──────────────────────────────────────────────────
    const glowCount = Math.round(MAP_DECORATION_CONFIG.glowingDetailCount * density);
    for (let i = 0; i < glowCount; i += 1) {
      const y = module.y + rng.range(module.height * 0.05, module.height * 0.95);
      const pathX = getPathX(y);
      // Place on screen (including pathways or margins)
      const x = pathX + rng.range(-140, 140);

      const key = rng.pick([AssetKeys.deco.mushrooms, AssetKeys.deco.cracks, AssetKeys.deco.mark]);
      const isFlat = key !== AssetKeys.deco.mushrooms;
      const size = key === AssetKeys.deco.mushrooms ? rng.range(42, 60) : rng.range(76, 112);
      addDeco(key, x, y, size, size, rng.range(-0.4, 0.4), false, !isFlat);
    }

    // ── 6. PATH DETAILS ─────────────────────────────────────────────────────
    // Micro details directly on/around the path to make the ground feel alive
    const pathSteps = Math.floor(10 * MAP_DECORATION_CONFIG.pathDetailDensity * density);
    for (let i = 0; i < pathSteps; i += 1) {
      const t = i / (pathSteps - 1 || 1);
      const y = module.y + module.height * t + rng.range(-15, 15);
      const pathX = getPathX(y);
      const x = pathX + rng.range(-54, 54);

      // Random micro-detail key
      const key = rng.pick([
        AssetKeys.deco.puddle,      // Toxic mud/oil puddle
        AssetKeys.deco.cracks,      // Green energy cracks
        AssetKeys.deco.skull,       // Stray skull
        AssetKeys.deco.bones,       // Broken bone pile
        AssetKeys.deco.grass        // Small tufts of grass
      ]);

      const isFlat = key === AssetKeys.deco.puddle || key === AssetKeys.deco.cracks || key === AssetKeys.deco.grass;
      let width = rng.range(32, 64);
      let height = width;

      // Adjust sizes for flat puddles or cracks to be slightly larger
      if (key === AssetKeys.deco.puddle) {
        width = rng.range(64, 108);
        height = width * rng.range(0.56, 0.74);
      } else if (key === AssetKeys.deco.cracks) {
        width = rng.range(76, 120);
        height = width * 0.86;
      }

      addDeco(key, x, y, width, height, rng.range(-0.8, 0.8), false, !isFlat);
    }
  }
}
